import { inject, Injectable } from '@angular/core';
import { ProductService } from '@epicuro-next/data/product';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import * as configStripState from '@epicuro-next/state/config-strip-state';
import {
  memoizeProductSizeData,
  ProductExtendedVariant,
  selectMemoProductSizesV2,
} from '@epicuro-next/state/config-strip-state';
import { selectPreferredScaleSet } from '@epicuro-next/state/user-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { EMPTY, finalize } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

type ProductData = {
  articleId: string;
  brandId: string;
  loadPrepacks: boolean;
};

interface State {
  product: ProductData | null;
  loading: boolean;
}

@Injectable()
export class PreviewSizeGridComponentStore extends ComponentStore<State> {
  private store = inject(Store);
  private productService = inject(ProductService);

  private readonly product$ = this.select(({ product }) => product).pipe(
    filter(isNonNullish),
  );

  public readonly differentMaterialDimensions$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.DifferentMaterialDimensions
        ],
      ),
    ),
    map(Boolean),
  );

  public readonly cachedProduct$ = this.product$.pipe(
    switchMap(({ articleId, brandId }) =>
      this.store.select(
        selectMemoProductSizesV2({
          brandId,
          articleId,
        }),
      ),
    ),
    filter(isNonNullish),
  );

  public isLoadingProduct$ = this.select(({ loading }) => loading);

  constructor() {
    super({
      product: null,
      loading: false,
    });
  }

  public loadProduct$ = this.effect<ProductData>((payload$) => {
    return payload$.pipe(
      switchMap((product) => {
        return this.store
          .select(
            selectMemoProductSizesV2({
              brandId: product.brandId,
              articleId: product.articleId,
            }),
          )
          .pipe(
            take(1),
            filter(isNullish),
            map(() => product),
          );
      }),
      switchMap(({ articleId, brandId, loadPrepacks }) => {
        this.patchState({
          loading: true,
        });
        return this.productService
          .loadSlimProduct(articleId, brandId, loadPrepacks)
          .pipe(
            tap((product) => {
              if (!product) {
                throw new Error('Cannot find product');
              }
            }),
            filter(isNonNullish),
            withLatestFrom(this.store.select(selectPreferredScaleSet)),
            tap(([product, selectedScaleSet]) => {
              const scaleSets = product.scaleSets ?? {
                default: '',
                scales: {},
              };
              this.store.dispatch(
                memoizeProductSizeData({
                  articleId: product.productId,
                  brandId: product.brandId,
                  materials: product.children as ProductExtendedVariant[],
                  prepacks: product.prepacks ?? [],
                  availableScaleSets: scaleSets,
                  scaleSetKeys: Object.keys(scaleSets.scales ?? {}).sort(),
                  selectedScale:
                    selectedScaleSet && scaleSets.scales[selectedScaleSet]
                      ? selectedScaleSet
                      : scaleSets.default || '',
                }),
              );
              this.store.dispatch(configStripState.selectNowDeliveryPeriod());
            }),
            catchError(() => {
              return EMPTY;
            }),
            finalize(() => {
              this.patchState({
                loading: false,
              });
            }),
          );
      }),
      takeUntil(
        this.store
          .select(configStripState.selectIsConfigStripOpen)
          .pipe(filter(Boolean)),
      ),
    );
  })(this.product$);

  public setProduct(product: ProductData) {
    this.patchState({
      product,
    });
  }
}
